import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { CompanyId } from '../../alerts/models/Alert';
import { Timestamp } from '../../shared/common.types';
import { useToaster } from '../../shared/composables/use-toaster';
import { UISocketDataMessage } from '../../shared/models/UISocket';
import { UserId } from '../../shared/models/User';
import { SocketAction, SocketActionHandler } from '../../shared/services/SocketActionHandler';
import { SiteId } from '../../site-detail/models/Site';
import { VideoCallToastActionComponent } from '../components/video-call-toast-action/video-call-toast-action.component';
import { VIDEO_CALL_STATUS, VideoCallId } from '../models/video-call';
import { VIDEO_CALL_EVENT_TYPE } from '../models/video-call-event';
import { getVideoCallToasterId } from '../utils/getVideoCallToasterId';
import { VideoCallSessionStore } from '../video-call-session.store';
import { VideoCallsStore } from '../video-calls.store';

interface VideoCallEventMessage extends UISocketDataMessage {
    companyId: CompanyId;
    siteId: SiteId;
}

export interface UISocketVideoCallInvitationSentEventMessage extends VideoCallEventMessage {
    callId: VideoCallId;
    userId: UserId;
    createdAt: Timestamp;
    url: string;
    ownerToken: string;
}

export interface UISocketJoinedParticipantVideoCallEventMessage extends VideoCallEventMessage {
    callId: string;
    userName: string;
    sessionId: string;
    url: string;
    ownerToken: string;
    isOwner: boolean;
}

export interface UISocketLeftParticipantVideoCallEventMessage extends VideoCallEventMessage {
    userName: string;
    sessionId: string;
    isOwner: boolean;
}

export type UISocketHungUpVideoCallEventMessage = VideoCallEventMessage;

@Injectable()
export class VideoCallInvitationSentHandler implements SocketActionHandler {
    readonly action: SocketAction = 'invitationSent';

    private readonly videoCalls = inject(VideoCallsStore);

    handle(event: UISocketVideoCallInvitationSentEventMessage): void {
        this.videoCalls.invitationSent({
            id: event.callId,
            companyId: event.companyId,
            siteId: event.siteId,
            installerId: event.userId,
            createdAt: event.createdAt,
            url: event.url,
            ownerToken: event.ownerToken,
            status: VIDEO_CALL_STATUS.INVITATION_SENT,
        });
    }
}

/** When the client joins, a toast offers to join the call until the client leaves. */
@Injectable()
export class VideoCallJoinedParticipantHandler implements SocketActionHandler {
    readonly action: SocketAction = 'joinedParticipant';

    private readonly videoCalls = inject(VideoCallsStore);
    private readonly translate = inject(TranslateService);
    private readonly toaster = useToaster();

    handle(event: UISocketJoinedParticipantVideoCallEventMessage): void {
        this.videoCalls.participantJoined(event.siteId, event.isOwner);
        if (event.isOwner) return;

        this.toaster.info({
            id: getVideoCallToasterId(event.siteId),
            title: this.translate.instant('GUEST_JOINED_VIDEO_CALL', { userName: event.userName }),
            duration: 0,
            component: VideoCallToastActionComponent,
            componentInputs: { siteId: event.siteId },
        });
    }
}

@Injectable()
export class VideoCallLeftParticipantHandler implements SocketActionHandler {
    readonly action: SocketAction = 'leftParticipant';

    private readonly videoCalls = inject(VideoCallsStore);
    private readonly toaster = useToaster();

    handle(event: UISocketLeftParticipantVideoCallEventMessage): void {
        this.videoCalls.participantLeft(event.siteId, event.isOwner);
        if (!event.isOwner) this.toaster.dismiss(getVideoCallToasterId(event.siteId));
    }
}

@Injectable()
export class VideoCallHungUpHandler implements SocketActionHandler {
    readonly action: SocketAction = VIDEO_CALL_EVENT_TYPE.HUNG_UP;

    private readonly videoCalls = inject(VideoCallsStore);
    private readonly session = inject(VideoCallSessionStore);

    handle(event: UISocketHungUpVideoCallEventMessage): void {
        this.videoCalls.stopped(event.siteId);
        void this.session.ended(event.siteId);
    }
}
